import React from 'react'
import { FieldArray, Field, useFormikContext } from 'formik'
import { Box, Button, Columns, Element, Heading, Icon, Message } from 'react-bulma-components'
import InputField from '../../../form/fields/input'
import CheckboxField from '../../../form/fields/checkbox'
import { RemoveIcon } from '../../../icons'
import {
  ContractualScheduleEntryFormValue,
  createNewContractualScheduleEntry,
} from './contractual-schedules-utils'
import { dateToDateInputValue } from '../../../../utils/date'
import { subDays } from 'date-fns'
import { ContractualWeekView } from './contractual-schedules-week-view'

interface ContractualSchedulesSectionProps {
  name: string // Formik field name (e.g., "newUsers.0.contractualSchedules")
  hasWorkerEverHadTimeSlots?: boolean // True if worker has ever had non-empty time slots from API
}

/**
 * Main component for editing contractual schedules
 */
const ContractualSchedulesSection: React.FC<ContractualSchedulesSectionProps> = ({
  name,
  hasWorkerEverHadTimeSlots = false,
}) => {
  const { values, setFieldValue } = useFormikContext<any>()
  // Support both nested (newUsers.0.contractualSchedules) and direct (contractualSchedules) paths
  const schedules: ContractualScheduleEntryFormValue[] =
    values.newUsers?.[0]?.contractualSchedules || values.contractualSchedules || []

  const addNewSchedule = () => {
    // Close the current active entry if it exists
    const activeIndex = schedules.findIndex(s => !s.effectiveTo)
    if (activeIndex !== -1) {
      const activeSchedule = schedules[activeIndex]
      if (activeSchedule) {
        const newEffectiveFrom = new Date()
        const previousDay = subDays(newEffectiveFrom, 1)
        setFieldValue(`${name}.${activeIndex}.effectiveTo`, dateToDateInputValue(previousDay))
      }
    }

    // Add new active entry
    const newSchedule = createNewContractualScheduleEntry()
    setFieldValue(`${name}`, [...schedules, newSchedule])
  }

  return (
    <Element>
      <Heading size={5} mb={4}>
        Horaires Contractuels
      </Heading>
      <Element textSize={7} mb={4}>
        Définissez les horaires de travail contractuels pour ce travailleur. Vous pouvez créer
        plusieurs périodes pour gérer l'historique des changements d'horaires.
      </Element>

      <FieldArray name={name}>
        {({ remove }) => (
          <>
            {schedules.map((schedule, scheduleIndex) => {
              return (
                <Box key={scheduleIndex} mb={5}>
                  <Columns mb={4}>
                    <Columns.Column size={4}>
                      <Field
                        label="Date de début"
                        name={`${name}.${scheduleIndex}.effectiveFrom`}
                        component={InputField}
                        type="date"
                        required
                      />
                    </Columns.Column>
                    <Columns.Column size={4}>
                      <Field
                        label={`Date de fin (optionnel - période active)`}
                        name={`${name}.${scheduleIndex}.effectiveTo`}
                        component={InputField}
                        type="date"
                      />
                    </Columns.Column>
                    <Columns.Column size={2}>
                      <Field
                        label="Heures/semaine"
                        name={`${name}.${scheduleIndex}.weeklyHoursWorked`}
                        component={InputField}
                        type="number"
                        required
                      />
                    </Columns.Column>
                    <Columns.Column size={2}>
                      <Field
                        label="Heures/mois"
                        name={`${name}.${scheduleIndex}.monthlyHoursWorked`}
                        component={InputField}
                        type="number"
                        required
                      />
                    </Columns.Column>
                  </Columns>

                  <Columns mb={4}>
                    <Columns.Column size={12}>
                      <Field
                        component={CheckboxField}
                        label="Utiliser les créneaux horaires"
                        name={`${name}.${scheduleIndex}.useTimeSlots`}
                        disabled={hasWorkerEverHadTimeSlots}
                      />
                    </Columns.Column>
                  </Columns>

                  {schedule.useTimeSlots !== false ? (
                    <Element mb={4}>
                      <ContractualWeekView basePath={name} scheduleIndex={scheduleIndex} />
                    </Element>
                  ) : (
                    <Message color="warning" mb={4}>
                      <Message.Body>
                        <strong>Attention :</strong> Sans créneaux horaires, les journées de travail
                        avec événement prestation (ex: congé payé) seront calculées sur la base de
                        l'horaire théorique de la journée de travail et non pas sur le contrat.{' '}
                        <br />
                      </Message.Body>
                    </Message>
                  )}

                  {schedules.length > 1 && (
                    <Element display="flex" justifyContent="flex-end" mt={3}>
                      <Button
                        color="danger"
                        size="small"
                        onClick={() => remove(scheduleIndex)}
                        type="button"
                      >
                        <Icon>
                          <RemoveIcon color="white" />
                        </Icon>
                        <Element renderAs="span" ml={1}>
                          Supprimer la période
                        </Element>
                      </Button>
                    </Element>
                  )}
                </Box>
              )
            })}

            <Button onClick={addNewSchedule} color="primary" outlined type="button">
              Ajouter une période
            </Button>
          </>
        )}
      </FieldArray>
    </Element>
  )
}

export default ContractualSchedulesSection
